import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  closePeriod,
  createBatch,
  createJournalEntry,
  fetchBatches,
  fetchCoaMapping,
  fetchExportPreview,
  fetchJournalEntries,
  fetchJournalEntry,
  fetchNeedsReview,
  fetchPeriodEvents,
  fetchPeriods,
  fetchPostableAccounts,
  fetchRelatedJournalEntries,
  reopenPeriod,
  retryEntry,
} from "@/api/accounting";
import type { CreateJournalEntryPayload, JournalEntryListQuery } from "@/interfaces/accounting";

/** The Journal Entries blotter. Keyed on the whole query so a date change refetches. */
export function useJournalEntries(query: JournalEntryListQuery = {}) {
  return useQuery({
    queryKey: ["journalEntries", query],
    queryFn: () => fetchJournalEntries(query),
  });
}

/** One journal entry for the show page. */
export function useJournalEntry(id: number | null) {
  return useQuery({
    queryKey: ["journalEntry", id],
    queryFn: () => fetchJournalEntry(id as number),
    enabled: id != null,
  });
}

/** The account picker. Reference data — safe to hold for the session. */
export function usePostableAccounts() {
  return useQuery({
    queryKey: ["postableAccounts"],
    queryFn: fetchPostableAccounts,
    staleTime: 5 * 60 * 1000,
  });
}

/** Posts a hand-keyed entry and refreshes the blotter. */
export function useCreateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateJournalEntryPayload) => createJournalEntry(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["journalEntries"] }),
  });
}

/** Every accounting period. Cheap and small — the periods screen's spine. */
export function usePeriods(range?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: ["accountingPeriods", range ?? {}],
    queryFn: () => fetchPeriods(range),
  });
}

/** What a period would export right now. Refetched after every action that could change it. */
export function useExportPreview(period: string | null) {
  return useQuery({
    queryKey: ["exportPreview", period],
    queryFn: () => fetchExportPreview(period as string),
    enabled: period != null,
  });
}

/** Batches for one period. */
export function useBatches(period: string | null) {
  return useQuery({
    queryKey: ["postingBatches", period],
    queryFn: () => fetchBatches(period ?? undefined),
    enabled: period != null,
  });
}

/**
 * Everything that changes a period's export state invalidates the same three queries. Kept in one
 * place because forgetting one leaves a screen showing a batch that has already been accepted, or a
 * preview still offering entries that just went out.
 */
function useRefreshPeriod() {
  const qc = useQueryClient();
  return () => {
    // Prefix, not exact key: accepting a batch from the cross-period blotter must also refresh the
    // per-period view, and vice versa. An exact key leaves one of them showing a stale state.
    qc.invalidateQueries({ queryKey: ["exportPreview"] });
    qc.invalidateQueries({ queryKey: ["postingBatches"] });
    qc.invalidateQueries({ queryKey: ["accountingPeriods"] });
    qc.invalidateQueries({ queryKey: ["periodEvents"] });
    qc.invalidateQueries({ queryKey: ["journalEntries"] });
  };
}

export function useCreateBatch(period: string | null) {
  const refresh = useRefreshPeriod();
  return useMutation({
    mutationFn: () => createBatch(period as string),
    onSuccess: refresh,
  });
}

export function useClosePeriod(period: string | null) {
  const refresh = useRefreshPeriod();
  return useMutation({ mutationFn: () => closePeriod(period as string), onSuccess: refresh });
}

export function useReopenPeriod(period: string | null) {
  const refresh = useRefreshPeriod();
  return useMutation({ mutationFn: () => reopenPeriod(period as string), onSuccess: refresh });
}

/** The Needs Review blotter, and the count that badges its tab. */
export function useNeedsReview() {
  return useQuery({ queryKey: ["needsReview"], queryFn: fetchNeedsReview });
}

/** The Chart of Account Mapper. Static reference data — bindings only change by migration/seed. */
export function useCoaMapping() {
  return useQuery({ queryKey: ["coaMapping"], queryFn: fetchCoaMapping });
}

/**
 * Retry resolution on a held entry. Invalidates broadly: a resolved entry leaves Needs Review,
 * joins its period's exportable set, and changes that period's held count — three screens.
 */
export function useRetryEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => retryEntry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["needsReview"] });
      qc.invalidateQueries({ queryKey: ["exportPreview"] });
      qc.invalidateQueries({ queryKey: ["accountingPeriods"] });
      qc.invalidateQueries({ queryKey: ["periodEvents"] });
      qc.invalidateQueries({ queryKey: ["journalEntries"] });
    },
  });
}

/** A period's activity feed. Refreshed by the same prefix every period action invalidates. */
export function usePeriodEvents(period: string | null) {
  return useQuery({
    queryKey: ["periodEvents", period],
    queryFn: () => fetchPeriodEvents(period as string),
    enabled: period != null,
  });
}

/**
 * A document's journal entries. `sourceType` null disables the query, which is how the panel stays
 * silent for anyone without finance access rather than firing a request that would 403.
 */
export function useRelatedJournalEntries(sourceType: number | null, sourceId: number) {
  return useQuery({
    queryKey: ["relatedJournalEntries", sourceType, sourceId],
    queryFn: () => fetchRelatedJournalEntries(sourceType as number, sourceId),
    enabled: sourceType != null && sourceId > 0,
  });
}
